/**
 * News API functions that match the updated backend schema
 */

import {
  NewsArticle,
  NewsArticleResponse,
  NewsCategoriesResponse,
  NewsCategory,
  NewsListResponse,
} from '../types/news';
import { STRAPI_URL } from './base';

/**
 * Fetch news articles with pagination and filtering
 */
export async function fetchNews(params?: {
  page?: number;
  pageSize?: number;
  category?: string;
  featured?: boolean;
}): Promise<NewsListResponse> {
  try {
    const searchParams = new URLSearchParams();

    // Add pagination parameters
    if (params?.page) {
      searchParams.append('pagination[page]', params.page.toString());
    }
    if (params?.pageSize) {
      searchParams.append('pagination[pageSize]', params.pageSize.toString());
    }

    // Add filtering parameters
    if (params?.category) {
      searchParams.append('filters[category][slug][$eq]', params.category);
    }
    if (params?.featured) {
      searchParams.append('filters[featured][$eq]', 'true');
    }

    // Add population for related data
    searchParams.append('populate[featuredImage]', 'true');
    searchParams.append('populate[category]', 'true');
    searchParams.append('populate[seo]', 'true');

    // Add sorting - publishedDate first, then createdAt
    searchParams.append('sort[0]', 'publishedDate:desc');
    searchParams.append('sort[1]', 'createdAt:desc');

    const url = `${STRAPI_URL}/api/news-articles?${searchParams.toString()}`;

    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...(process.env.NEXT_PUBLIC_STRAPI_API_TOKEN && {
          Authorization: `Bearer ${process.env.NEXT_PUBLIC_STRAPI_API_TOKEN}`,
        }),
      },
      next: { revalidate: 60 }, // Cache for 1 minute
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('News API error:', response.status, errorText);
      throw new Error(
        `Failed to fetch news: ${response.status} - ${errorText}`
      );
    }

    const result: NewsListResponse = await response.json();
    return result;
  } catch (error) {
    console.error('Error fetching news:', error);
    throw error;
  }
}

/**
 * Fetch a single news article by slug or ID
 * Enhanced with better error handling and slug support
 */
export async function fetchNewsArticle(
  slug: string
): Promise<NewsArticle | null> {
  try {
    const searchParams = new URLSearchParams();

    // Add population for related data
    searchParams.append('populate[featuredImage]', 'true');
    searchParams.append('populate[category]', 'true');
    searchParams.append('populate[seo]', 'true');

    const url = `${STRAPI_URL}/api/news-articles/${slug}?${searchParams.toString()}`;

    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...(process.env.NEXT_PUBLIC_STRAPI_API_TOKEN && {
          Authorization: `Bearer ${process.env.NEXT_PUBLIC_STRAPI_API_TOKEN}`,
        }),
      },
      next: { revalidate: 60 }, // Cache for 1 minute
    });

    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      const errorText = await response.text();
      console.error('News article API error:', response.status, errorText);
      throw new Error(
        `Failed to fetch news article: ${response.status} - ${errorText}`
      );
    }

    const result: NewsArticleResponse = await response.json();
    return result.data;
  } catch (error) {
    console.error('Error fetching news article:', error);
    throw error;
  }
}

/**
 * Fetch news categories with enhanced error handling
 */
export async function fetchNewsCategories(): Promise<NewsCategory[]> {
  try {
    const searchParams = new URLSearchParams();

    // Add population for news count
    searchParams.append('populate[news]', 'true');

    // Add sorting by order and name
    searchParams.append('sort[0]', 'order:asc');
    searchParams.append('sort[1]', 'name:asc');

    const url = `${STRAPI_URL}/api/news-categories?${searchParams.toString()}`;

    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...(process.env.NEXT_PUBLIC_STRAPI_API_TOKEN && {
          Authorization: `Bearer ${process.env.NEXT_PUBLIC_STRAPI_API_TOKEN}`,
        }),
      },
      next: { revalidate: 300 }, // Cache for 5 minutes
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('News categories API error:', response.status, errorText);
      throw new Error(
        `Failed to fetch news categories: ${response.status} - ${errorText}`
      );
    }

    const result: NewsCategoriesResponse = await response.json();
    return result.data;
  } catch (error) {
    console.error('Error fetching news categories:', error);
    throw error;
  }
}

/**
 * Fetch featured news articles with enhanced filtering
 */
export async function fetchFeaturedNews(): Promise<NewsArticle[]> {
  try {
    const searchParams = new URLSearchParams();

    // Filter for featured articles only
    searchParams.append('filters[featured][$eq]', 'true');

    // Limit to 6 featured articles
    searchParams.append('pagination[pageSize]', '6');

    // Add population for related data
    searchParams.append('populate[featuredImage]', 'true');
    searchParams.append('populate[category]', 'true');

    // Add sorting by publishedDate and createdAt
    searchParams.append('sort[0]', 'publishedDate:desc');
    searchParams.append('sort[1]', 'createdAt:desc');

    const url = `${STRAPI_URL}/api/news-articles?${searchParams.toString()}`;

    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...(process.env.NEXT_PUBLIC_STRAPI_API_TOKEN && {
          Authorization: `Bearer ${process.env.NEXT_PUBLIC_STRAPI_API_TOKEN}`,
        }),
      },
      next: { revalidate: 300 }, // Cache for 5 minutes
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Featured news API error:', response.status, errorText);
      throw new Error(
        `Failed to fetch featured news: ${response.status} - ${errorText}`
      );
    }

    const result: NewsListResponse = await response.json();
    return result.data;
  } catch (error) {
    console.error('Error fetching featured news:', error);
    throw error;
  }
}

/**
 * Fetch related news articles (excluding current article)
 * Enhanced with better category filtering
 */
export async function fetchRelatedNews(
  currentArticle: NewsArticle,
  limit: number = 3
): Promise<NewsArticle[]> {
  try {
    const searchParams = new URLSearchParams();

    // Filter by same category if available
    if (currentArticle.category && currentArticle.category.slug) {
      searchParams.append(
        'filters[category][slug][$eq]',
        currentArticle.category.slug
      );
    } else if (currentArticle.category && currentArticle.category.id) {
      searchParams.append(
        'filters[category][id][$eq]',
        currentArticle.category.id.toString()
      );
    }

    // Increase page size to account for potential filtering of current article
    // This ensures we still get the desired number of results after filtering
    searchParams.append('pagination[pageSize]', (limit + 1).toString());

    // Add population for related data
    searchParams.append('populate[featuredImage]', 'true');
    searchParams.append('populate[category]', 'true');

    // Add sorting by publishedDate and createdAt
    searchParams.append('sort[0]', 'publishedDate:desc');
    searchParams.append('sort[1]', 'createdAt:desc');

    const url = `${STRAPI_URL}/api/news-articles?${searchParams.toString()}`;

    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...(process.env.NEXT_PUBLIC_STRAPI_API_TOKEN && {
          Authorization: `Bearer ${process.env.NEXT_PUBLIC_STRAPI_API_TOKEN}`,
        }),
      },
      next: { revalidate: 300 }, // Cache for 5 minutes
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Related news API error:', response.status, errorText);
      throw new Error(
        `Failed to fetch related news: ${response.status} - ${errorText}`
      );
    }

    const result: NewsListResponse = await response.json();

    // Filter out the current article to ensure it's not included in results
    const filteredData = result.data.filter(
      article => article.id !== currentArticle.id
    );

    // Return only the requested number of articles
    return filteredData.slice(0, limit);
  } catch (error) {
    console.error('Error fetching related news:', error);
    throw error;
  }
}
